import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Eye, CheckCircle2, ExternalLink, StickyNote } from "lucide-react";
import api from "../lib/api";

const BUCKETS = [
  { key: "changes_requested", label: "Changes Requested", cls: "bg-purple-100 text-purple-700" },
  { key: "expired", label: "Expired", cls: "bg-red-100 text-red-700" },
  { key: "expiring", label: "Expiring Soon", cls: "bg-amber-100 text-amber-700" },
  { key: "viewed_silent", label: "Viewed, No Response", cls: "bg-cyan-100 text-cyan-700" },
  { key: "never_viewed", label: "Never Viewed", cls: "bg-blue-100 text-blue-700" },
];

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

function DoneRow({ item, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/followups/${item.quote_id}/done`, { note });
      toast.success(`${item.quote_number} follow-up marked done`);
      onDone();
    } catch { toast.error("Failed to mark done"); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap gap-2 items-center bg-slate-50 rounded-lg p-3">
      <input data-testid={`followup-note-input-${item.quote_id}`} className="pg-input !py-2 flex-1 min-w-[220px] text-[13px]"
        placeholder="Optional note (e.g. called customer, left voicemail)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
      <button data-testid={`followup-done-confirm-${item.quote_id}`} onClick={submit} disabled={busy}
        className="text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60">
        {busy ? "Saving..." : "Confirm Done"}
      </button>
    </div>
  );
}

function FollowUpRow({ item, onChange }) {
  const [showDone, setShowDone] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const b = BUCKETS.find((x) => x.key === item.bucket);
  const resend = async () => {
    setSending(true);
    try {
      await api.post(`/admin/customer-quotes/${item.quote_id}/send`);
      toast.success(`${item.quote_number} re-sent to ${item.customer_email}`);
      onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Resend failed"); }
    setSending(false);
  };
  return (
    <div data-testid={`followup-row-${item.quote_number}`} className="border border-slate-200 rounded-xl p-4 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-[Poppins] font-bold text-[15px] text-[var(--pg-navy)]">{item.quote_number}</span>
            <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-[Poppins] font-bold ${b.cls}`}>{b.label.toUpperCase()}</span>
            <span className="text-[12px] text-slate-400">{item.rfq_number}</span>
          </div>
          <div className="mt-1 text-[13px] text-slate-500">
            <strong className="text-[var(--pg-navy)] font-medium">{item.customer_company || item.customer_name}</strong> · {money(item.total)}
            {item.bucket === "expired" && item.expires_on
              ? <span className="ml-2 text-red-600 font-medium">Expired {Math.max(0, Math.floor((Date.now() - new Date(item.expires_on).getTime()) / 86400000))}d ago ({item.expires_on})</span>
              : <>
                  <span className="ml-2 text-amber-700 font-medium">{item.days_waiting}d waiting</span>
                  {item.expires_on && <span className="ml-2">Expires {item.expires_on}</span>}
                </>}
          </div>
          <div className="mt-0.5 text-[12px] text-slate-400 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {item.view_count > 0
              ? <span className="inline-flex items-center gap-1"><Eye size={12} /> Viewed ×{item.view_count}, last {String(item.last_viewed_at).slice(0, 10)}</span>
              : "Never opened by customer"}
            {item.assigned_to && <span className="text-slate-500">· Assigned: {item.assigned_to}</span>}
            {item.changes_requested_message && <span className="w-full text-purple-700 italic">Customer: "{item.changes_requested_message}"</span>}
            {(item.followup_notes || []).slice(-2).map((n, i) => (
              <span key={i} className="w-full text-slate-500">📝 {n.note} <span className="text-slate-400">— {n.by}, {(n.at || "").slice(0, 10)}</span></span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid={`followup-resend-${item.quote_id}`} onClick={resend} disabled={sending}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3 py-2 rounded-lg bg-[var(--pg-accent)] text-white hover:bg-[var(--pg-navy)] disabled:opacity-60">
            <Mail size={13} /> {sending ? "Sending..." : "Resend Quote"}
          </button>
          <Link data-testid={`followup-view-rfq-${item.quote_id}`} to={`/admin/rfqs/${item.rfq_id}`}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">
            <ExternalLink size={13} /> View RFQ
          </Link>
          <button data-testid={`followup-add-note-${item.quote_id}`} onClick={() => setShowNote((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">
            <StickyNote size={13} /> Note
          </button>
          <button data-testid={`followup-done-${item.quote_id}`} onClick={() => setShowDone((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3 py-2 rounded-lg bg-green-100 text-green-700 hover:bg-green-200">
            <CheckCircle2 size={13} /> Done
          </button>
        </div>
      </div>
      {showNote && (
        <div className="mt-3 flex flex-wrap gap-2 items-center bg-slate-50 rounded-lg p-3">
          <input data-testid={`followup-note-only-input-${item.quote_id}`} className="pg-input !py-2 flex-1 min-w-[220px] text-[13px]" autoFocus
            placeholder="Follow-up note (e.g. left voicemail, will call back Friday)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={800} />
          <button data-testid={`followup-note-save-${item.quote_id}`} disabled={!note.trim()}
            onClick={async () => {
              try { await api.post(`/admin/followups/${item.quote_id}/note`, { note }); toast.success("Note added"); setShowNote(false); setNote(""); onChange(); }
              catch { toast.error("Failed to add note"); }
            }}
            className="text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-[var(--pg-navy)] text-white hover:bg-[var(--pg-accent)] disabled:opacity-50">Save Note</button>
        </div>
      )}
      {showDone && <div className="mt-3"><DoneRow item={item} onDone={onChange} /></div>}
    </div>
  );
}

export default function FollowUps() {
  const [data, setData] = useState(null);
  const [bucket, setBucket] = useState("");
  const load = useCallback(() => {
    api.get("/admin/followups").then((r) => setData(r.data)).catch(() => setData({ counts: {}, items: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-slate-500 p-8" data-testid="followups-loading">Loading follow-ups...</div>;
  const items = bucket ? data.items.filter((i) => i.bucket === bucket) : data.items;

  return (
    <div data-testid="admin-followups">
      <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)]">Follow-Ups</h1>
      <p className="mt-1 text-[13.5px] text-slate-500">Quotes that need attention — resend, call, or mark handled.</p>

      <div className="mt-5 flex flex-wrap gap-2">
        <button data-testid="followup-tab-all" onClick={() => setBucket("")}
          className={`px-3.5 py-2 rounded-lg text-[12.5px] font-[Poppins] font-semibold ${!bucket ? "bg-[var(--pg-navy)] text-white" : "bg-white border border-slate-200 text-slate-500 hover:text-[var(--pg-navy)]"}`}>
          All ({data.counts.total || 0})
        </button>
        {BUCKETS.map((b) => (
          <button key={b.key} data-testid={`followup-tab-${b.key}`} onClick={() => setBucket(b.key)}
            className={`px-3.5 py-2 rounded-lg text-[12.5px] font-[Poppins] font-semibold ${bucket === b.key ? "bg-[var(--pg-navy)] text-white" : "bg-white border border-slate-200 text-slate-500 hover:text-[var(--pg-navy)]"}`}>
            {b.label} ({data.counts[b.key] || 0})
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {items.length === 0 && (
          <div data-testid="followups-empty" className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400 text-[14px]">
            Nothing needs follow-up right now. 🎉
          </div>
        )}
        {items.map((i) => <FollowUpRow key={i.quote_id} item={i} onChange={load} />)}
      </div>
    </div>
  );
}
